import React, { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Form, Formik } from 'formik';
import Alert from '@material-ui/lab/Alert';

import CatalogRequestMetadata from './CatalogRequestMetadata';
import CatalogRequestFields from './ListFields/CatalogRequestFields';
import Button from 'components/Common/Button/Button';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import Loading from 'components/Common/Loading/Loading';
import { userContext } from 'contexts/userContext';
import { useCustomer } from 'hooks/useCustomer';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useEnv from 'hooks/useEnv';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import validationSchema from 'schemas/CatalogRequestFormErrors';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { getRoutesUri } from 'services/generic';
import { gtmAfiEventLab } from 'services/gtm';
import { GTM_FORM_CATALOG_REQUEST } from 'settings/gtm/gtmForm';
import { GTM_PAGE_ACCOUNT, GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';

import styles from './CatalogRequest.module.scss';

type Message = {
  message: string;
  severity: 'success' | 'error';
};

export default function CatalogRequest(): JSX.Element {
  const { t } = useTranslation(['translation', 'catalogRequestPage']);
  const { state, pathname } = useLocation();
  const { gtmUserCookie } = useContext(userContext);
  const { customer } = useCustomer();
  const { isUk } = useCurrentCountry();
  const navigate = useNavigate();
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '453px' });
  const [isLoading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>();
  const { urlApiCustomer } = useEnv();
  const initialValues = useMemo(
    () => ({
      turnstileToken: '',
      civility: customer?.civilityNumber || '',
      lastName: customer?.lastName || '',
      firstName: customer?.firstName || '',
      email: customer?.email || state?.email || '',
      nameWay: customer?.addresses?.P.street || '',
      nameComplement: customer?.addresses?.P.nameComplement || '',
      nameAddress: customer?.addresses?.P.nameAddress || '',
      locality: customer?.addresses?.P.saidPlace || '',
      postCode: customer?.addresses?.P.postalCode || '',
      city: customer?.addresses?.P.city || '',
      country: isUk ? t('LABEL_UNITED_KINGDOM') : t('LABEL_FRANCE'),
      landLine: customer?.phone || '',
      mobileNumber: customer?.cellPhone || '',
    }),
    [customer, state]
  );

  useGtmPageView(
    GTM_PAGE_OTHER,
    `Afibel - ${t('CATALOG_REQUEST_META_TITLE', {
      ns: 'catalogRequestPage',
    })}`
  );

  const onSubmit = async (values: typeof initialValues) => {
    setMessages(undefined);
    setLoading(true);

    const customerExist = await fetcherWithBody(`${urlApiCustomer}/exists`, {
      email: values.email,
    });

    if (customerExist.exists && !customer?.isLoggedIn) {
      navigate(getRoutesUri('customerLogin') as string, {
        state: { mandatoryLogInCatalogRequest: true },
      });

      return;
    }

    try {
      const response = (await submitCatalogRequest(values)) as Response;

      if (!response.ok) {
        const json = await response.json();

        if (json.error) {
          setMessages([{ message: json.error, severity: 'error' }]);
        } else if (json.errors && Object.keys(json.errors).length > 0) {
          setMessages(
            Object.values(json.errors).map(message => ({
              message,
              severity: 'error',
            })) as Message[]
          );
        } else {
          setMessages([
            {
              message: t('LABEL_CATALOG_REQUEST_SENT_ERROR', {
                ns: 'catalogRequestPage',
              }),
              severity: 'error',
            },
          ]);
        }

        return;
      }

      setMessages([
        {
          message: t('LABEL_CATALOG_REQUEST_SENT_SUCCESS_CONTENT', {
            ns: 'catalogRequestPage',
          }),
          severity: 'success',
        },
      ]);

      gtmAfiEventLab(
        pathname,
        'afiEvent',
        'Demande de catalogue',
        'Inscription',
        gtmUserCookie
      );
    } catch {
      setMessages([
        {
          message: t('LABEL_CATALOG_REQUEST_SENT_ERROR', {
            ns: 'catalogRequestPage',
          }),
          severity: 'error',
        },
      ]);
    } finally {
      setLoading(false);
      turnstile.current?.reset();
    }
  };

  const submitCatalogRequest = async (values: typeof initialValues) => {
    const request = {
      ...values,
      phone: replacePhoneNumberWithoutSpace(values.landLine || ''),
      cellPhone: replacePhoneNumberWithoutSpace(values.mobileNumber || ''),
      addresses: {
        P: {
          type: 'P',
          nameAddress: values.nameAddress || '',
          nameComplement: values.nameComplement || '',
          street: values.nameWay,
          saidPlace: values.locality,
          postalCode: values.postCode,
          city: values.city,
          country: values.country,
        },
      },
    };

    return await fetcherWithBody(apiUrls.getCatalogRequest(), request, true);
  };

  return (
    <div className={styles.container}>
      <CatalogRequestMetadata />
      <h1 className={styles.title}>{t('LABEL_MYACCOUNT_CATALOG')}</h1>
      {messages?.length && (
        <div className={styles.alert}>
          <ScrollToTop />
          {messages.map(({ message, severity }, i) => (
            <Alert key={i} severity={severity}>
              {message}
            </Alert>
          ))}
        </div>
      )}
      <div className={styles.border}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(isUk, t)}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          <Form noValidate>
            <FormikErrorsGtmLogger
              page_type={GTM_PAGE_ACCOUNT}
              form_type={GTM_FORM_CATALOG_REQUEST}
            />
            <CatalogRequestFields />
            <TurnstileFormikRow
              classes={{
                row: styles.captchaRow,
                errorMessage: styles.captchaErrorMessage,
              }}
              turnstileRef={turnstile}
              turnstileProps={{
                className: styles.captchaField,
                options: {
                  action: 'catalog_request_page',
                  size: isTurnstileDesktop ? 'normal' : 'compact',
                },
              }}
            />
            <div className={styles.actionToolbar}>
              <Button>
                {t('LABEL_RECEIVE_CATALOGUE', {
                  ns: 'catalogRequestPage',
                })}
              </Button>
            </div>
            <RequiredFields />
            <ScrollToFieldError />
          </Form>
        </Formik>
      </div>
      {isLoading && <Loading loading />}
    </div>
  );
}
